import { Tooltip as AntTooltip } from "antd";
import styled from "styled-components";

// #region STYLES
const TooltipContent = styled.div`
    background: ${({ $backgroundColor }) =>
        $backgroundColor || "var(--backgrounds-lines-n-600, #10141b)"};
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    line-height: 14px;
    padding: 0 4px;
    text-align: left;
`;

export const TooltipTitle = styled.div`
    color: var(--text-icons-gray-100, #d1d1d1);
    font-size: 12px;
    font-weight: 700;
`;

const TooltipBody = styled.div`
    color: var(--text-icons-gray-100, #d1d1d1);
    font-size: 12px;
    font-weight: 400;
`;
// #endregion

/** A wrapper around AntTooltip with custom styles and default props
 * @param {Object} props - The component props.
 * @param {string} props.header - The header of the tooltip
 * @param {string} props.title - The title of the tooltip
 * @param {React.ReactNode} props.children - The children of the tooltip
 * @param {string} props.placement - The placement of the tooltip
 * @param {string} props.color - The color of the tooltip
 * @param {number} props.mouseEnterDelay - The delay before the tooltip is shown
 * @param {string} props.overlayClassName - The class name of the tooltip
 * @param {boolean} props.disabled - If true, tooltip will not be shown
 * @param {object} props.overlayStyle - The style of the tooltip
 * @param {string} props.width - The width of the tooltip
 * @param {boolean} props.centered - Centers the tooltip content over its trigger
 * @param {"default"|"table-cell"} props.variant - Uses the standard table-cell tooltip appearance
 * @returns {JSX.Element} - The tooltip
 */
const TooltipSimple = ({
    header,
    title,
    children,
    placement = "top",
    color,
    mouseEnterDelay,
    overlayClassName = "custom-tooltip",
    disabled = false,
    overlayStyle,
    width = "250px",
    centered = false,
    variant = "default"
}) => {
    if (variant === "table-cell") {
        return (
            <AntTooltip
                placement={placement}
                color={color ?? "var(--backgrounds-lines-n-200)"}
                mouseEnterDelay={mouseEnterDelay ?? 0.1}
                overlayStyle={overlayStyle}
                title={disabled ? "" : title}>
                {children}
            </AntTooltip>
        );
    }

    const tooltipColor = color ?? "var(--backgrounds-lines-n-600)";
    const hasHeader = header !== null && header !== undefined && header !== "";
    const hasTitle = title !== null && title !== undefined && title !== "";
    const tooltipTitle =
        hasHeader || hasTitle ? (
            <TooltipContent $backgroundColor={tooltipColor}>
                {hasHeader && <TooltipTitle>{header}</TooltipTitle>}

                {hasTitle && <TooltipBody style={{ marginTop: hasHeader ? "4px" : "0px" }}>{title}</TooltipBody>}
            </TooltipContent>
        ) : null;

    const overlayInnerStyle = {
        ...overlayStyle,
        width,
        height: "100%",
        ...(centered
            ? {}
            : {
                  left: "20%",
                  transform: "translateX(-20%)"
              })
    };

    return (
        <AntTooltip
            title={tooltipTitle}
            trigger={disabled ? "" : "hover"}
            defaultOpen={false}
            overlayClassName={overlayClassName}
            color={tooltipColor}
            mouseEnterDelay={mouseEnterDelay ?? 0.3}
            //prevent default from children
            onMouseEnter={e => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onMouseLeave={e => {
                e.preventDefault();
                e.stopPropagation();
            }}
            placement={placement}
            overlayInnerStyle={overlayInnerStyle}>
            {children}
        </AntTooltip>
    );
};

export default TooltipSimple;
